import { Button, Loader, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listHolidays } from "graphql/queries";
import { updateHoliday } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { HolidayCreateForm, HolidayUpdateForm } from ".";

const Holidays = () => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "holiday:create",
    "holiday:update",
    "holiday:delete",
  ]);
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayDetails, setHolidayDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    holidayId: null,
    holidayName: null,
  });
  const [modalHoliday, setModalHoliday] = useState({
    visible: false,
    holidayId: null,
  });
  const columnsMapped = useMemo(
    () =>
      holidays.map((item) => ({
        ...item,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                holidayDetails?.id === item.id
                  ? strings.holidays.hide
                  : strings.holidays.show
              }
              variation="link"
              size="small"
              onClick={() => {
                holidayDetails?.id === item.id
                  ? setHolidayDetails(null)
                  : setHolidayDetails(item);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.holidays.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.holidays.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    holidayId: item.id,
                    holidayName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [holidays, holidayDetails?.id]
  );

  const onNext = () => {
    fetchHolidays(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchHolidays(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchHolidays(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalHoliday({ visible: true, action, holidayId: id });
  const onSuccessHoliday = () => {
    setModalHoliday({ visible: false });
    fetchHolidays();
  };

  async function fetchHolidays(token, isNext = true, keywords) {
    try {
      setHolidaysLoading(true);
      const holidaysData = await API.graphql(
        graphqlOperation(listHolidays, {
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      date: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const holidaysItems = holidaysData.data.listHolidays.items;
      setHolidays(holidaysItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: holidaysData.data.listHolidays.nextToken,
        };
      });
      setHolidaysLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setHolidaysLoading(false);
    }
  }
  async function holidayDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateHoliday, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateHoliday.id;
      setHolidays(holidays.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        holidayId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      key: "date",
      label: strings.table.headers.date,
      _style: { width: "30%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "50%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.holidays.addHoliday}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <div style={styles.searchByKeyword}>
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </div>
        {holidaysLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!holidayDetails?.id} style={styles.collapseCard}>
          <CCard key={holidayDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{holidayDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.date}:</b> {holidayDetails?.date}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(holidayDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, holidayId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.holidayName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, holidayId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => holidayDelete(modalDetails.holidayId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalHoliday.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalHoliday.visible}
          onClose={() => setModalHoliday({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalHoliday.action === "create"
                ? strings.modalHoliday.addTitle
                : strings.modalHoliday.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalHoliday.action === "create" ? (
                <HolidayCreateForm onSuccess={onSuccessHoliday} />
              ) : (
                <HolidayUpdateForm
                  holidayId={modalHoliday.holidayId}
                  onSuccess={onSuccessHoliday}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Días festivos",
    successDeleted: "Día festivo eliminado con éxito",
    holidays: {
      addHoliday: "Agregar Día festivo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar día festivo",
    },
    table: {
      headers: {
        name: "Nombre",
        date: "Fecha",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Día festivo",
      date: "Fecha",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el día festivo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalHoliday: {
      addTitle: "Agregar Día festivo",
      updateTitle: "Actualizar Día festivo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Holidays;
